import { useState } from 'react';
import { Container, Panel, Input, Button, Form, Message } from 'rsuite';
import { useAuth } from './utils/useAuth';
import { getApiUrl, getAuthHeaders } from './utils/apiConfig';

/**
 * Validates user_id according to rules:
 * - Only letters (a-z, A-Z), numbers (0-9), and hyphens (-)
 * - Cannot start or end with a hyphen
 * - Cannot contain two consecutive hyphens
 */
function validateUserId(userId) {
  if (!userId || userId.trim().length === 0) {
    return 'User ID is required';
  }

  // Check for invalid characters (only allow letters, numbers, and hyphens)
  if (!/^[a-zA-Z0-9-]+$/.test(userId)) {
    return 'User ID can only contain letters (a-z, A-Z), numbers (0-9), and hyphens (-)';
  }

  // Cannot start with hyphen
  if (userId.startsWith('-')) {
    return 'User ID cannot start with a hyphen';
  }

  // Cannot end with hyphen
  if (userId.endsWith('-')) {
    return 'User ID cannot end with a hyphen';
  }

  // Cannot contain two consecutive hyphens
  if (userId.includes('--')) {
    return 'User ID cannot contain two consecutive hyphens';
  }

  // Minimum length check
  if (userId.length < 3) {
    return 'User ID must be at least 3 characters long';
  }

  // Maximum length check
  if (userId.length > 30) {
    return 'User ID must be at most 30 characters long';
  }

  return null; // Valid
}

function UserIdSetup() {
  const auth = useAuth();
  const [user_id, setUserId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError('');
    
    // Validate user_id
    const userIdError = validateUserId(user_id);
    if (userIdError) {
      setError(userIdError);
      return;
    }

    setLoading(true);

    try {
      const token = await auth.getAccessTokenSilently();
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const formData = new FormData();
      formData.append('user_id', user_id.trim());
      
      const response = await fetch(getApiUrl('/api/users/me/user_id'), {
        method: 'PUT',
        headers: {
          'Authorization': headers['Authorization']
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 409) {
          // User ID already taken
          const errorMsg = data.detail?.message || data.detail?.error || 'User ID already taken';
          throw new Error(errorMsg);
        }
        
        // Extract error message - handle both object and string formats
        let errorMsg = 'Failed to set user ID';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (data.detail.message) {
            errorMsg = data.detail.message;
          } else if (data.detail.error) {
            errorMsg = data.detail.error;
          }
        }
        
        // Add helpful guidance for Management API errors
        if (errorMsg.includes('Management API token') || errorMsg.includes('Management API')) {
          errorMsg += '. This is a server configuration issue. Please contact support or check Auth0 Management API credentials.';
        }
        
        throw new Error(errorMsg);
      }

      setSuccess(true);
      setError('');
      
      // Force Auth0 to refresh user data by calling getUser with cache disabled
      // This ensures the updated user_metadata is fetched from Auth0
      if (auth.getUser) {
        try {
          // Call getUser to refresh the user profile with latest metadata
          await auth.getUser();
        } catch (err) {
          console.warn('Failed to refresh user data:', err);
        }
      }
      
      // Wait a bit to ensure Auth0 has updated, then reload
      // The reload will trigger App.js to fetch fresh user profile
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('User ID setup error:', err);
      setError(err.message || 'Failed to set user ID. Please try again.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Panel header="User ID Set Successfully" bordered style={{ width: 400 }}>
          <Message type="success" showIcon>
            User ID set successfully! Redirecting...
          </Message>
        </Panel>
      </Container>
    );
  }

  return (
    <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Panel header="Set Your User ID" bordered style={{ width: 400 }}>
        <Form fluid onSubmit={handleSubmit}>
          {error && (
            <Message type="error" showIcon style={{ marginBottom: 20 }}>
              <div>
                <strong>Error:</strong> {error}
                {error.includes('Management API') && (
                  <div style={{ marginTop: 10, fontSize: '12px', opacity: 0.9 }}>
                    This error indicates a server configuration problem with Auth0 Management API credentials. 
                    The server administrator needs to verify that AUTH0_MGMT_CLIENT_ID and AUTH0_MGMT_CLIENT_SECRET 
                    are correctly configured and the Machine-to-Machine application has the required scopes.
                  </div>
                )}
              </div>
            </Message>
          )}

          <Message type="warning" showIcon style={{ marginBottom: 20 }}>
            <strong>User ID is required.</strong> You must set a User ID before you can access the application.
          </Message>
          
          <p style={{ marginBottom: 20 }}>
            Please choose a unique User ID that will be used to identify you in the system.
          </p>

          <Form.Group>
            <Form.ControlLabel>User ID</Form.ControlLabel>
            <Input
              value={user_id}
              onChange={(value) => setUserId(value)}
              placeholder="e.g., john-doe123"
            />
            <Form.HelpText>
              Letters, numbers, and hyphens only. Cannot start/end with hyphen or contain consecutive hyphens.
            </Form.HelpText>
          </Form.Group>

          <Form.Group>
            <Button appearance="primary" type="submit" block loading={loading}>
              Set User ID
            </Button>
          </Form.Group>
        </Form>
      </Panel>
    </Container>
  );
}

export default UserIdSetup;

